
import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';

interface User {
  id: string;
  email: string;
}

interface GameStats {
  totalGames: number;
  wins: number;
  losses: number;
  totalRewards: number;
  currentStreak: number;
  bestStreak: number;
  pepcoinsEarned: number;
  pepcoinsSpent: number;
}

interface GameStatsProps {
  user: User | null;
  gameStats: GameStats;
}

interface DetailedStats {
  winRate: number;
  averageReward: number;
  profitMargin: number;
  gamesThisWeek: number;
  gamesThisMonth: number;
  favoriteGameType: string;
  totalPlayTime: number;
}

export default function GameStats({ user, gameStats }: GameStatsProps) {
  const [loading, setLoading] = useState(false);

  // Calculate detailed statistics with null safety
  const detailedStats = useMemo(() => {
    const totalGames = (gameStats?.totalGames || 0);
    const gamesWon = (gameStats?.wins || 0);
    const totalEarnings = (gameStats?.pepcoinsEarned || 0);
    const totalBets = (gameStats?.pepcoinsSpent || 0);
    
    return {
      winRate: totalGames > 0 ? (gamesWon / totalGames) * 100 : 0,
      averageBet: totalGames > 0 ? (totalBets / totalGames) : 0,
      averageWin: gamesWon > 0 ? (totalEarnings / gamesWon) : 0,
      profitLoss: totalEarnings - totalBets,
      roi: totalBets > 0 ? ((totalEarnings - totalBets) / totalBets) * 100 : 0
    };
  }, [gameStats]);

  const StatCard = ({ title, value, subtitle, icon, color, trend }: any) => (
    <div className={`bg-gradient-to-br ${color} backdrop-blur-sm border border-gray-700/50 rounded-xl p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center`}>
          <i className={`${icon} text-xl text-white`}></i>
        </div>
        {trend && (
          <span className={`text-sm font-bold px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="text-2xl font-black text-white mb-1">{value}</div>
      <div className="text-sm text-gray-400">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Games"
          value={(gameStats?.totalGames || 0).toLocaleString()}
          icon="ri-gamepad-line"
          color="from-purple-500/20 to-pink-500/20"
          trend={12}
        />
        <StatCard
          title="Win Rate"
          value={`${(detailedStats?.winRate || 0).toFixed(1)}%`}
          icon="ri-trophy-line"
          color="from-green-500/20 to-emerald-500/20"
          trend={5}
        />
        <StatCard
          title="Total Earnings"
          value={`${(gameStats?.pepcoinsEarned || 0).toLocaleString()} PEP`}
          icon="ri-coins-line"
          color="from-yellow-500/20 to-orange-500/20"
          trend={8}
        />
        <StatCard
          title="Current Streak"
          value={`${(gameStats?.currentStreak || 0).toLocaleString()}`}
          icon="ri-fire-line"
          color="from-red-500/20 to-pink-500/20"
          trend={-2}
        />
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-bar-chart-line text-blue-500 mr-2"></i>
            Performance Overview
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Games Won</span>
              <span className="font-semibold text-green-600">{(gameStats?.wins || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Games Lost</span>
              <span className="font-semibold text-red-600">{(gameStats?.losses || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Best Streak</span>
              <span className="font-semibold text-purple-600">{(gameStats?.bestStreak || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Bet</span>
              <span className="font-semibold text-blue-600">{(detailedStats?.averageBet || 0).toFixed(2)} PEP</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Win</span>
              <span className="font-semibold text-green-600">{(detailedStats?.averageWin || 0).toFixed(2)} PEP</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Profit/Loss</span>
              <span className={`font-semibold ${(detailedStats?.profitLoss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(detailedStats?.profitLoss || 0) >= 0 ? '+' : ''}{(detailedStats?.profitLoss || 0).toFixed(2)} PEP
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ROI</span>
              <span className={`font-semibold ${(detailedStats?.roi || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(detailedStats?.roi || 0) >= 0 ? '+' : ''}{(detailedStats?.roi || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-trophy-line text-yellow-500 mr-2"></i>
            Achievements
          </h3>
          <div className="space-y-3">
            <div className={`flex items-center p-3 rounded-lg ${(gameStats?.totalGames || 0) >= 10 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <i className={`ri-gamepad-line mr-3 ${(gameStats?.totalGames || 0) >= 10 ? 'text-green-500' : 'text-gray-400'}`}></i>
              <div>
                <div className="font-medium text-gray-900">First Steps</div>
                <div className="text-sm text-gray-600">Play 10 games</div>
              </div>
              {(gameStats?.totalGames || 0) >= 10 && <i className="ri-check-line text-green-500 ml-auto"></i>}
            </div>
            
            <div className={`flex items-center p-3 rounded-lg ${(gameStats?.wins || 0) >= 5 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <i className={`ri-trophy-line mr-3 ${(gameStats?.wins || 0) >= 5 ? 'text-green-500' : 'text-gray-400'}`}></i>
              <div>
                <div className="font-medium text-gray-900">Winner</div>
                <div className="text-sm text-gray-600">Win 5 games</div>
              </div>
              {(gameStats?.wins || 0) >= 5 && <i className="ri-check-line text-green-500 ml-auto"></i>}
            </div>
            
            <div className={`flex items-center p-3 rounded-lg ${(gameStats?.bestStreak || 0) >= 3 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <i className={`ri-fire-line mr-3 ${(gameStats?.bestStreak || 0) >= 3 ? 'text-green-500' : 'text-gray-400'}`}></i>
              <div>
                <div className="font-medium text-gray-900">Hot Streak</div>
                <div className="text-sm text-gray-600">Win 3 games in a row</div>
              </div>
              {(gameStats?.bestStreak || 0) >= 3 && <i className="ri-check-line text-green-500 ml-auto"></i>}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="ri-line-chart-line text-purple-500 mr-2"></i>
          Performance Trends
        </h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <i className="ri-bar-chart-line text-4xl text-gray-400 mb-2"></i>
            <p className="text-gray-600">Performance chart will be available after playing more games</p>
          </div>
        </div>
      </div>
    </div>
  );
}
